import { useState } from "react";
import { Button } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { LocationAlias } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateLocationAliasMutation, useDeleteLocationAliasMutation } from "@/api/locationApi/locationAlias";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import LocationAliasFormDialog from "./LocationAliasFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface LocationAliasListTableProps {
  data: LocationAlias[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function LocationAliasListTable({ data, pagination, onPageChange }: LocationAliasListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState<LocationAlias | undefined>();

  const [updateLocationAlias] = useUpdateLocationAliasMutation();
  const [deleteLocationAlias] = useDeleteLocationAliasMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (alias: LocationAlias) => {
    setSelectedAlias(alias);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: LocationAlias) => {
    if (!selectedAlias) return;
    try {
      await updateLocationAlias({ ...data, id: selectedAlias.id } as LocationAlias).unwrap();
      toast.success(t("temporalActivity:locationAlias.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedAlias(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (alias: LocationAlias) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:locationAlias.confirmDeleteTitle"),
      description: t("temporalActivity:locationAlias.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteLocationAlias(alias.id).unwrap();
          toast.success(t("temporalActivity:locationAlias.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "id", label: t("temporalActivity:locationAlias.ref") },
    { key: "alias_text", label: t("temporalActivity:locationAlias.aliasText") },
    {
      key: "resolvesTo",
      label: t("temporalActivity:locationAlias.resolvesTo"),
      render: (row: LocationAlias) => (
        <span>
          {row.canonical_city
            ? `${t("temporalActivity:locationAlias.cityPrefix")}: ${row.canonical_city.city_name}`
            : row.canonical_district
              ? `${t("temporalActivity:locationAlias.districtPrefix")}: ${row.canonical_district.district_name}`
              : "—"}
        </span>
      ),
    },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: LocationAlias) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <LocationAliasFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedAlias}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
